import { Loader2, Paperclip } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { useUploadedLorPreview } from '@/components/claims-filing/lor/hooks/useUploadedLorPreview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetUploadedLorMarkdownQuery } from '@/services/api/coverageService';
import type { LorUploadedArtifactSummary } from '@/services/types/coverage-types';

type UploadedLorFileChipProps = {
  matterId: string;
  coverageId: string;
  artifact: LorUploadedArtifactSummary;
  onDownload: () => void;
};

export const UploadedLorFileChip: React.FC<UploadedLorFileChipProps> = ({
  matterId,
  coverageId,
  artifact,
  onDownload,
}) => {
  const [open, setOpen] = useState(false);
  const {
    url: previewUrl,
    isLoading: isPreviewLoading,
    error: previewError,
    request: requestPreview,
  } = useUploadedLorPreview(matterId, coverageId, Boolean(artifact));

  const {
    data: markdownData,
    error: markdownError,
    isFetching: isMarkdownFetching,
    refetch: refetchMarkdown,
  } = useGetUploadedLorMarkdownQuery({ matterId, coverageId }, { skip: !open });

  useEffect(() => {
    if (open) {
      requestPreview();
    }
  }, [open, requestPreview]);

  const filename =
    artifact.original_filename || artifact.name || 'Uploaded LOR';
  const markdown = markdownData?.markdown ?? null;
  const markdownLength = markdownData?.length ?? 0;
  const markdownTruncated = markdownData?.is_truncated ?? false;
  const isMarkdownProcessing =
    (markdownError as { status?: number } | undefined)?.status === 409;

  const markdownCaption = useMemo(() => {
    if (!markdown) return null;
    if (markdownTruncated) {
      return `Showing first ${markdown.length.toLocaleString()} of ${markdownLength.toLocaleString()} characters`;
    }
    return `Total characters: ${markdownLength.toLocaleString()}`;
  }, [markdown, markdownLength, markdownTruncated]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2 border-dashed"
        onClick={() => setOpen(true)}
      >
        <Paperclip className="h-4 w-4" />
        <span className="truncate max-w-[180px]">{filename}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{filename}</DialogTitle>
            <DialogDescription>
              Review, preview, or download the uploaded Letter of
              Representation.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="preview" className="mt-4">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="preview">PDF Preview</TabsTrigger>
              <TabsTrigger value="markdown">Markdown Sent</TabsTrigger>
              <TabsTrigger value="download">Download</TabsTrigger>
            </TabsList>
            <TabsContent value="preview" className="mt-4">
              {isPreviewLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Rendering PDF preview…
                </div>
              ) : previewUrl ? (
                <object
                  className="h-[400px] w-full rounded border"
                  data={previewUrl}
                  type="application/pdf"
                >
                  <p className="text-sm text-muted-foreground p-4">
                    Preview unavailable.{' '}
                    <button
                      type="button"
                      className="text-blue-600 underline"
                      onClick={() => window.open(previewUrl, '_blank')}
                    >
                      Open in new tab
                    </button>
                    .
                  </p>
                </object>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {previewError
                    ? 'Preview failed to load.'
                    : 'Preview not available yet. Try again shortly.'}
                </div>
              )}
            </TabsContent>
            <TabsContent value="markdown" className="mt-4">
              {isMarkdownFetching ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading markdown…
                </div>
              ) : markdown ? (
                <>
                  {markdownCaption && (
                    <p className="text-xs text-muted-foreground mb-2">
                      {markdownCaption}
                    </p>
                  )}
                  <div className="rounded border bg-muted/60 p-3 h-72 overflow-auto whitespace-pre-wrap text-xs font-mono">
                    {markdown}
                  </div>
                </>
              ) : (
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>
                    {isMarkdownProcessing
                      ? 'The markdown is still processing. This usually finishes within a minute.'
                      : 'Markdown unavailable. Try refreshing.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchMarkdown()}
                  >
                    Refresh
                  </Button>
                </div>
              )}
            </TabsContent>
            <TabsContent value="download" className="mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                Download the original uploaded file.
              </p>
              <Button onClick={onDownload}>Download</Button>
            </TabsContent>
          </Tabs>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
